<template>
  <div class="element-ui-container absolute-cover">
    <template v-if="resizable && element.resizable && !isSliderDragging">
      <!-- rotation handle -->
      <div
        :class="[
          'absolute z-50 cursor-grab active:cursor-grabbing transition-opacity',
          useAlternateRotationHandlePosition
            ? 'right-0 top-1/2 -translate-y-1/2 translate-x-10'
            : 'left-1/2 bottom-0 -translate-x-1/2 translate-y-10',
          (isRotating || isDragging) ? 'opacity-0' : 'opacity-100'
        ]"
        @pointerdown.stop="props.onDragStart?.($event, element, SURFACE_EDITOR_DRAG_HANDLE.rotate)"
      >
        <div class="w-7 h-7 bg-white dark:bg-charcoal-800 rounded-full border border-blue-950 dark:border-charcoal-500 hover:bg-blue-400 dark:hover:bg-charcoal-700 transition-colors flex items-center justify-center mb-1">
          <u-icon name="i-ri-loop-left-line" class="text-sm text-blue-950 dark:text-white" />
        </div>
      </div>

      <!-- drag handles -->
      <div
        v-for="corner in dragCorners"
        :key="corner.handle"
        class="absolute -translate-x-1/2 -translate-y-1/2 bg-white hover:bg-blue-400 transition-colors rounded-full w-4 h-4 border border-blue-950 z-50"
        :class="corner.class"
        :style="{ cursor: corner.cursor }"
        @pointerdown.stop="props.onDragStart?.($event, element, corner.handle)"
      />

      <div
        v-for="edge in dragEdges"
        :key="edge.handle"
        class="absolute z-50 group"
        :class="edge.class"
        :style="{ cursor: edge.cursor }"
        @pointerdown.stop="props.onDragStart?.($event, element, edge.handle)"
      >
        <div class="absolute bg-heroblue" :class="edge.lineClass" />
        <div class="absolute bg-white rounded border border-blue-950 transition-colors group-hover:bg-blue-400" :class="edge.handleClass" />
      </div>

    </template>

    <div v-else-if="!isSliderDragging" class="absolute-cover border border-heroblue pointer-events-none" />
  </div>
</template>

<script setup>
  //todo: name this component better as it is no longer the ui container
  import {storeToRefs} from 'pinia';
  import {normalizeRotation} from '~/utils/math.js';

  const props = defineProps({
    element: Object,
    resizable: Boolean,
    onDragStart: Function
  });

  const surfaceEditorStore = useSurfaceEditorStore();
  const {isSliderDragging, isRotating, isContextBarAbove, isDragging} = storeToRefs(surfaceEditorStore);

  /**
   * Get the appropriate resize cursor based on element rotation.
   * Cursors cycle through 4 directions every 180° (45° per step).
   */
  function getRotatedCursor(baseCursor) {
    const rotation = props.element.rotation || 0;
    // Normalize rotation to 0-180 range (cursors repeat every 180°)
    const normalizedRotation = ((rotation % 180) + 180) % 180;
    // Calculate step (0-3) based on 45° increments
    const step = Math.round(normalizedRotation / 45) % 4;

    const cursorCycle = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];
    const baseIndex = cursorCycle.indexOf(baseCursor);
    if (baseIndex === -1) return baseCursor;

    const newIndex = (baseIndex + step) % 4;
    return cursorCycle[newIndex];
  }

  const dragCorners = computed(() => [
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.topLeft,
      class: 'top-0 left-0',
      cursor: getRotatedCursor('nwse-resize')
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.topRight,
      class: 'top-0 left-full',
      cursor: getRotatedCursor('nesw-resize')
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottomLeft,
      class: 'top-full left-0',
      cursor: getRotatedCursor('nesw-resize')
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottomRight,
      class: 'top-full left-full',
      cursor: getRotatedCursor('nwse-resize')
    }
  ]);

  const dragEdges = computed(() => [
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.left,
      class: 'top-0 bottom-0 left-0 w-4 my-2 -translate-x-1/2',
      cursor: getRotatedCursor('ew-resize'),
      lineClass: 'top-0 bottom-0 left-1/2 w-0.5 -translate-x-1/2',
      handleClass: 'top-1/2 left-1/2 w-2 h-6 -translate-x-1/2 -translate-y-1/2'
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottom,
      class: 'bottom-0 left-0 right-0 h-4 mx-2 translate-y-1/2',
      cursor: getRotatedCursor('ns-resize'),
      lineClass: 'left-0 right-0 top-1/2 h-0.5 -translate-y-1/2',
      handleClass: 'left-1/2 top-1/2 w-6 h-2 -translate-x-1/2 -translate-y-1/2'
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.right,
      class: 'top-0 bottom-0 right-0 w-4 my-2 translate-x-1/2',
      cursor: getRotatedCursor('ew-resize'),
      lineClass: 'top-0 bottom-0 left-1/2 w-0.5 -translate-x-1/2',
      handleClass: 'top-1/2 left-1/2 w-2 h-6 -translate-x-1/2 -translate-y-1/2'
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.top,
      class: 'top-0 left-0 right-0 h-4 mx-2 -translate-y-1/2',
      cursor: getRotatedCursor('ns-resize'),
      lineClass: 'left-0 right-0 top-1/2 h-0.5 -translate-y-1/2',
      handleClass: 'left-1/2 top-1/2 w-6 h-2 -translate-x-1/2 -translate-y-1/2'
    }
  ]);


  const useAlternateRotationHandlePosition = computed(() => {
    const normalizedRotation = normalizeRotation(props.element.rotation);

    return isContextBarAbove.value
      ? (normalizedRotation > 150 && normalizedRotation < 210)
      : (normalizedRotation < 30 || normalizedRotation > 330);
  });

</script>
